import type { ReactNode } from 'react';
import { http } from '@/shared/api/client';
import type { ClientPlatform } from '@/shared/lib/device';
import type { PassbookAddField } from '@/shared/types/passbook';

export const BFF_PUBLIC_INVITE_PREFIX = '/bff/public/invite';

export interface PublicInvitePassbook {
  idPassbook: string;
  inactive: boolean;
  clientName: string;
  title: string;
  organizationName: string;
  backgroundColor?: string;
  foregroundColor?: string;
  labelColor?: string;
  fieldUniqueIdLabel: string;
  validationUniqueId: boolean;
  hasPreRegister: boolean;
  linkTermsConditions?: string;
  termsAndConditions?: string;
  loyaltyRules: { label: string; value: string }[];
  slug?: string;
  hasPT?: boolean;
  hasES?: boolean;
  hasIN?: boolean;
  hasFlavor?: string;
  hasGWallet?: boolean;
  addFields?: PassbookAddField[];
  /** Código gerado no servidor (11 dígitos) — usado só na variação `hidden-generated` (ex.: abf/sqg). */
  generatedUniqueId: string;
  /** Cartão-presente (Fase 4) — ver model/invite-variant.ts `resolveGiftVariant`. */
  hasGiftcard?: boolean;
  /** Código do cartão-presente novo, gerado no servidor — usado só no fluxo `omatuto`. */
  generatedGiftCode: string;
  /** Módulos de e-mail/SMS marketing da loja: o formulário pede o aceite só desses canais. */
  marketingChannels?: { email: boolean; sms: boolean };
}

export interface InviteDepartment {
  idOffice: string;
  idDepartment: string;
}

/** Segmento de rota opcional pra lojas multi-loja/departamento (mesmo `invite.ejs`, URL com 2 segmentos a mais). */
function departmentPath(department?: InviteDepartment): string {
  return department ? `/${department.idOffice}/${department.idDepartment}` : '';
}

export function fetchPublicInvitePassbook(
  idPassbook: string,
  department?: InviteDepartment,
): Promise<PublicInvitePassbook> {
  return http.get(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}${departmentPath(department)}`);
}

export interface RegisterInviteInput {
  name: string;
  uniqueId: string;
  email: string;
  /** Vazio quando a loja não coleta (variação `hidden-generated`, ex.: abf/sqg). */
  birthday: string;
  phone: string;
  /** Vazio quando a loja não coleta (variação `hidden-generated`). */
  gender: string;
  platform: ClientPlatform;
  campanha?: string;
  /** DDI (ex.: "351") — só na variação `intl-phone`; back aplica `+{codpais}{phone}`. */
  codpais?: string;
  state?: string;
  city?: string;
  /**
   * Campos customizados (addFields) + estado/cidade — objeto plano; filhos
   * (circus) vêm como array de objetos de uma chave cada (ver server/routes/public-invite.ts).
   */
  hasAddFields?: boolean;
  fields?: Record<string, string> | Array<Record<string, string>>;
  /** Honeypot — deve ficar sempre vazio. */
  website?: string;
  /** Código do link de indicação (Indique e Ganhe) — o legado liga o cadastro à indicação. */
  mgmCode?: string;
  /**
   * Aceite de e-mail/SMS marketing, só dos canais que o formulário mostrou
   * (`true` marcou, `false` viu e não marcou). Vira `privacy.PROMOEMAIL/PROMOTELEFONE`.
   */
  marketingConsent?: { email?: boolean; sms?: boolean };
}

/**
 * Indicação vinda de um link "Indique e ganhe". O convite não conhece o MGM:
 * só mostra o banner, avisa quando o visitante se identificou e manda o
 * código junto no cadastro. Quem monta isto é a feature member-get-member.
 */
export interface InviteReferral {
  code: string;
  banner?: ReactNode;
  /** Visitante terminou de digitar o identificador (vira "Indicada" no MGM). */
  onIdentify?: (uniqueId: string) => void;
}

export type RegisterInviteResult =
  | { status: 'created'; idPassbook: string; serialNumber: string; typeAccess: 'iOS' | 'Android' }
  | { status: 'duplicate'; email: string }
  | { status: 'blocked' };

export function registerInvite(
  idPassbook: string,
  input: RegisterInviteInput,
  department?: InviteDepartment,
): Promise<RegisterInviteResult> {
  return http.post(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}${departmentPath(department)}/register`, input);
}

export function appleWalletCardUrl(idPassbook: string, serialNumber: string): string {
  return `${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/card/apple/${serialNumber}`;
}

export async function fetchGoogleWalletSaveUrl(idPassbook: string, serialNumber: string): Promise<string> {
  return http.get(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/card/google/${serialNumber}`);
}

/** Checagem de unicidade no blur (`passbook.validationUniqueId`) — legado `POST /validationUniqueId`. */
export async function validateUniqueIdAvailable(idPassbook: string, uniqueId: string): Promise<boolean> {
  try {
    await http.post(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/validate-unique-id`, { uniqueId });
    return true;
  } catch {
    return false;
  }
}

export interface StateOption {
  acronym: string;
}

export function fetchInviteStates(idPassbook: string): Promise<StateOption[]> {
  return http.get(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/states`);
}

export function fetchInviteCities(idPassbook: string, state: string): Promise<string[]> {
  return http.get(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/states/${encodeURIComponent(state)}`);
}

/** Cartão-presente novo (`omatuto`) — sem CPF/nascimento/gênero; `uniqueId` é gerado no servidor. */
export interface RegisterGiftCardInput {
  name: string;
  email?: string;
  phone: string;
  platform: ClientPlatform;
  campanha?: string;
  codpais?: string;
  /** "250,00" — vírgula decimal, igual ao input mascarado do legado. */
  valorGift?: string;
  website?: string;
}

export function registerGiftCard(
  idPassbook: string,
  input: RegisterGiftCardInput,
): Promise<RegisterInviteResult> {
  return http.post(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/register-gift`, input);
}

/** Resgate de cartão pré-carregado (`trintaeum`/`trintaeum-livre`) — nunca cria cadastro novo. */
export interface RedeemGiftCardInput {
  uniqueId: string;
  name: string;
  email?: string;
  phone: string;
  platform: ClientPlatform;
  campanha?: string;
  codpais?: string;
  /** Só trintaeum-livre. */
  valorGift?: string;
  website?: string;
}

export type RedeemGiftCardResult =
  | { status: 'emailed'; email: string }
  | { status: 'not-found' }
  | { status: 'blocked' };

export function redeemGiftCard(
  idPassbook: string,
  input: RedeemGiftCardInput,
): Promise<RedeemGiftCardResult> {
  return http.post(`${BFF_PUBLIC_INVITE_PREFIX}/${idPassbook}/redeem-gift-card`, input);
}
